import { Api, BOTS_BASE_URL, CHAINS_BASE_URL } from '@/api/index';
import { getCompressedImage } from '@/utilities';
import axios from 'axios';
import { Post, PublishPost } from 'ignite360-core';

/** Safe compression for post images; falls back to original if compression fails (e.g. odd MIME from imports). */
async function compressPostImage(file: File): Promise<File> {
  try {
    return await getCompressedImage(file);
  } catch {
    return file;
  }
}

/** Prefer server validation / error text from Nest (and legacy `details` when message was generic). */
export function describePostPublishError(error: unknown, fallback: string): string {
  const err = error as {
    response?: {
      data?: {
        message?: string | string[];
        details?: string;
      };
    };
  };
  const data = err.response?.data;
  if (!data) {
    return fallback;
  }

  const joinMsg = (m: string | string[] | undefined) => {
    if (m == null) {
      return '';
    }
    return Array.isArray(m) ? m.join('; ') : String(m);
  };

  const msg = joinMsg(data.message).trim();
  if (msg && msg !== 'Invalid request') {
    return msg.slice(0, 800);
  }

  if (typeof data.details === 'string' && data.details.trim()) {
    return data.details.trim().slice(0, 800);
  }

  if (msg) {
    return msg.slice(0, 800);
  }

  return fallback;
}

export interface PublishPostOptions {
  [key: string]: unknown;
  id: string;
  data: PublishPost & { selectedPlatforms?: string[] };
  images?: { horizontal: File | null; square: File | null };
  video?: File | null;
}

export interface PublishChainPostOptions extends PublishPostOptions {
  botIds: string[];
}

export class PostApi {
  static async loadBotPosts(botId: string): Promise<Post[]> {
    const config = Api.finalizeConfig({
      method: 'GET',
      url: `${BOTS_BASE_URL}/${botId}/posts`,
    });
    const result = await axios.request<Post[]>(config);
    if (result.status === 200 && result.data) {
      return result.data;
    }
    return [];
  }

  static async loadChainPosts(chainId: string): Promise<Post[]> {
    const config = Api.finalizeConfig({
      method: 'GET',
      url: `${CHAINS_BASE_URL}/${chainId}/posts`,
    });
    const result = await axios.request<Post[]>(config);
    if (result.status === 200 && result.data) {
      return result.data;
    }
    return [];
  }

  static async publishChainPost(options: PublishChainPostOptions): Promise<Post[]> {
    return (await PostApi.publishPost(CHAINS_BASE_URL, options)) as Post[];
  }

  static async publishBotPost(options: PublishPostOptions): Promise<Post> {
    return (await PostApi.publishPost(BOTS_BASE_URL, options)) as Post;
  }

  private static async publishPost(
    baseUrl: string,
    { id, data, images, video, botIds }: PublishPostOptions | PublishChainPostOptions,
  ): Promise<Post | Post[]> {
    const formData = new FormData();

    if (data.title) {
      formData.append('title', data.title);
    }
    if (data.content) {
      formData.append('content', data.content);
    }
    // Nested DTO requires both title and URL when callToAction is present — never send a partial object.
    const cta = data.callToAction;
    if (cta?.title?.trim() && cta?.url?.trim()) {
      formData.append('callToAction[title]', cta.title.trim());
      formData.append('callToAction[url]', cta.url.trim());
    }
    if (video) {
      formData.append('video', video);
    } else if (images?.square && images.horizontal) {
      const compressedHorizontal = await compressPostImage(images.horizontal);
      formData.append('imageHorizontal', compressedHorizontal);

      const compressedSquare = await compressPostImage(images.square);
      formData.append('imageSquare', compressedSquare);
    }

    if (Array.isArray(botIds)) {
      botIds.forEach((botId: string) => {
        formData.append('botIds[]', botId);
      });
    }

    if (Array.isArray(data.selectedPlatforms)) {
      data.selectedPlatforms.forEach((platform: string) => {
        formData.append('selectedPlatforms[]', platform);
      });
    }

    const config = Api.finalizeConfig({
      method: 'POST',
      url: `${baseUrl}/${id}/posts`,
      data: formData,
    });
    const result = await axios.request<Post | Post[]>(config);
    if (result.status >= 200 && result.status < 300 && result.data !== undefined) {
      return result.data as Post | Post[];
    }
    throw new Error('Could not publish posts');
  }
}
